import React, { Suspense, useEffect } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import "./assets/style/main.scss";
import Layout from "./components/layout/Layout";
import CallTable from "./pages/call-table/CallTable";
import CallDetails from "./pages/call-details/CallDetails";
import CallPlayerHiHi from "./pages/call-details/CallPlayerHiHi";
import Insights from "./pages/insight/Insights";
import Scorecards from "./pages/settings/scorecards/Scorecards";
import Topics from "./pages/settings/topics/Topics";
import CallResult from "./pages/settings/call-result/CallResult";
import useAuth from "./hooks/useAuth";
import { setUpAuthAxiosInterceptors } from "./utils/customAxios";
import ProtectedRoutes from "./components/ProtectedRoutes";
import SharedResourceRedirect from "./pages/SharedResourceRedirect";
import SharedRoutes from "./components/SharedRoutes";
import NotFoundError from "./pages/error/NotFoundError";
import InternalServerError from "./pages/error/InternalServerError";
import UnauthorizedAccess from "./pages/error/UnauthorizedAccess";
import SystemSettings from "./pages/settings/system-settings/SystemSettings";
import Changepassword from "./pages/auth/change-password/ChangePassword";
import Login from "./pages/auth/Login";
import UserControl from "./pages/settings/user-control/UserControl";
import NotificationSetup from "./pages/settings/notification-setup/NotificationSetup";
import UnauthorizedAccessWithMessage from "./pages/error/UnauthorizedAccessWithMessage";
import useCustomNavigation from "./hooks/useCustomNavigation";
import DemoLoginRegister from "./pages/demo/DemoLoginRegister";
import ServiceUnreachable from "./pages/error/ServiceUnreachable";
import useLocale from "./hooks/useLocale";
import useTranslation from "./hooks/useTranslation";
import { titleMap } from "./data/DocumentTitles";
import ScorecardManagement from "./pages/settings/scorecards/ScorecardManagement";
import UploadRecording from "./pages/settings/system-settings/UploadRecording";
import { ErrorBoundary } from "react-error-boundary";
import fallbackRender from "./components/ErrorBoundary";
import { Loader } from "@progress/kendo-react-indicators";
import LoadingOverlay from "./components/LoadingOverlay";
import BulkDownload from "./pages/bulk-download/BulkDownload";
import HiHiDevice from "./pages/hihi-device/HiHiDevice";
import ChatManagement from "./pages/Copilot/ChatManagement";
import MsTeamsApp from "./pages/msteams-app/MsTeamsApp";
const UserView = React.lazy(() => import("./pages/settings/user-control/user-management/user-profile/UserView"));
const ScorecardDetail = React.lazy(() => import("./pages/settings/scorecards/ScorecardDetail"));
const CustomerPlatform = React.lazy(() => import("./pages/customer-platform-detail/CustomerPlatform"));
const MsTeamsAdminAction = React.lazy(() => import("./pages/customer-platform-detail/MsTeamsAdminAction"));
const App: React.FC = () => {
  const trans = useTranslation("DocumentTitle");
  const localeCtx = useLocale();
  const location = useLocation();
  const navigate = useNavigate();
  const customNav = useCustomNavigation();
  const auth = useAuth();

  setUpAuthAxiosInterceptors(location, customNav, auth?.setUserAuthToken);

  useEffect(() => {
    if (!localeCtx?.selectedLocale?.current.componentTranslations["DocumentTitle"]) {
      trans.fetchTranslations("DocumentTitle");
    }
  }, [localeCtx?.selectedLocale]);

  useEffect(() => {
    if (location.pathname === "/") {
      navigate("/home", { replace: true });
    }
  }, []);

  useEffect(() => {
    //here we will add only those document titles -
    //1. which are not related to header like login page, call details page etc.
    //2. which are related to header but contains variable part in them like user profile page.

    for (const path in titleMap) {
      if (location.pathname.match(new RegExp(`^${path}(\\/.*)?$`))) {
        const [key, title] = titleMap[path];
        document.title = trans.fetchLabelKeyTranslation(key, title);
        break;
      }
    }
  }, [window.location.pathname, trans.translations])

  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        {/* public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/demo-login-register" element={<DemoLoginRegister />} />

        <Route path="/share" element={<SharedResourceRedirect />} />
        <Route path="/not-found" element={<NotFoundError />} />
        <Route path="/error" element={<InternalServerError />} />
        <Route path="/unauthorized" element={<UnauthorizedAccess />} />
        <Route
          path="/unauthorized-calldetail"
          element={<UnauthorizedAccessWithMessage />}
        />
        <Route path="/service-unreachable" element={<ServiceUnreachable />} />
        {/* <Route path="/resetpassword" element={<Resetpassword />} /> */}

        {/* shared resource routes */}
        <Route path="/shared" element={<SharedRoutes />}>
          <Route
            path="/shared/calldetail/:platformCallDetailId"
            element={<CallDetails sharedProp={true} />}
          />
        </Route>

        {/* protected routes */}
        <Route path="/" element={<ProtectedRoutes />}>
          <Route path="/search" element={<CallTable />} />
          <Route
            path="/calldetail/:platformCallDetailId"
            element={<CallDetails />}
          />
          <Route path="/home" element={<Insights />} />
          <Route path="/settings/user-control" element={<UserControl />} />
          <Route
            path="/settings/user-control/user/:userId"
            element={<ErrorBoundary FallbackComponent={fallbackRender}><Suspense fallback={<div><LoadingOverlay
              customStyle={{ position: "fixed", marginTop: "55px" }}
              themeColor={"light"}
              size={"medium"}
              loadingText={""}
            /></div>}><UserView /></Suspense></ErrorBoundary>}
          />
          <Route path="/settings/scorecards" element={<ScorecardManagement />} />
          <Route
            path="/settings/scorecardDetail/:scorecardId"
            element={<ErrorBoundary FallbackComponent={fallbackRender}><Suspense fallback={<div>
              <LoadingOverlay
                customStyle={{ position: "fixed", marginTop: "55px" }}
                themeColor={"light"}
                size={"medium"}
                loadingText={""}
              /></div>}>
              <ScorecardDetail /></Suspense></ErrorBoundary>}
          />
          <Route path="/settings/topics" element={<Topics />} />
          <Route path="/settings/outcomes" element={<CallResult />} />


          <Route path="/settings/system" element={<SystemSettings />} />

          <Route path="/changepassword" element={<Changepassword />} />
          <Route
            path="/settings/notification-setup"
            element={<NotificationSetup />}
          />
          <Route path="/customer-platform" element={<ErrorBoundary FallbackComponent={fallbackRender}><Suspense fallback={<div><LoadingOverlay
            customStyle={{ position: "fixed", marginTop: "55px" }}
            themeColor={"light"}
            size={"medium"}
            loadingText={""}
          /></div>}><CustomerPlatform /></Suspense></ErrorBoundary>} />
          <Route path="/customer-platform/admin-action" element={<ErrorBoundary FallbackComponent={fallbackRender}><Suspense fallback={<div><LoadingOverlay
            customStyle={{ position: "fixed", marginTop: "55px" }}
            themeColor={"light"}
            size={"medium"}
            loadingText={""}
          /></div>}><MsTeamsAdminAction /></Suspense></ErrorBoundary>} />
          <Route path="/import-recording" element={<UploadRecording />} />
          <Route path="/bulkDownload/:requestId" element={<BulkDownload />} />
          <Route path="/CIQpilot" element={<ChatManagement />} />
        </Route>

        {/* Catch all */}
        <Route path="*" element={<NotFoundError />} />
      </Route>
      <Route path="/call-player-hihi" element={<CallPlayerHiHi />} />
       <Route path="/msteams-app" element={<MsTeamsApp/>}/>
      <Route path="/hihi5/callplayer" element={<HiHiDevice />} />
    </Routes>
  );
};

export default App;
